import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FilterMatchMode, FilterOperator } from 'primereact/api';

const parseUTCTimestamp = (ts) => {
    if (!ts) return '—';
    const date = new Date(ts * 1000);
    if (isNaN(date.getTime())) return String(ts);
    return date.toLocaleString([], {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
};

const formatElapsed = (minutes) => {
    if (minutes == null) return '—';
    const total = parseFloat(minutes);
    if (isNaN(total)) return '—';
    const days = Math.floor(total / 1440);
    const hrs  = Math.floor((total % 1440) / 60);
    const mins = Math.floor(total % 60);
    if (days > 0) return `${days}d ${hrs}h ${mins}m`;
    if (hrs  > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
};

const statusBodyTemplate = (row) => {
    const isOnline = row.pingStatus === 'ONLINE';
    return (
        <span className={`ping-badge ${isOnline ? 'ping-badge--online' : 'ping-badge--down'}`}>
            <i className={`pi ${isOnline ? 'pi-check-circle' : 'pi-times-circle'}`} />
            {row.pingStatus || '—'}
        </span>
    );
};

const DevicePingTable = ({ devices = [], onRefresh, loading = false }) => {
    const pingTableRef = useRef(null);
    const [tableFilters, setTableFilters] = useState(null);

    useEffect(() => {
        setTableFilters({
            global:              { value: null, matchMode: FilterMatchMode.CONTAINS },
            deviceName:          { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            ipAddress:           { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            location:            { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            pingStatus:          { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            stateElapsedMinutes: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            runTimestamp:        { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="supply-chain-container">
            <div className="d-flex justify-content-end">
                <Button
                    className="p-button-text"
                    icon="pi pi-refresh"
                    tooltip="Refresh"
                    tooltipOptions={{ position: 'top' }}
                    onClick={onRefresh}
                    disabled={loading}
                />
                <Button
                    className="p-button-text"
                    icon="pi pi-download"
                    tooltip="Export CSV"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => pingTableRef.current.exportCSV()}
                />
            </div>
            <DataTable
                value={devices}
                ref={pingTableRef}
                showGridlines
                filters={tableFilters}
                filterDisplay="menu"
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage="No devices found"
            >
                <Column field="deviceName" header="Device Name"    sortable filter />
                <Column field="ipAddress"  header="IP Address"     sortable filter />
                <Column field="location"   header="Asset Location" sortable filter />
                <Column field="pingStatus" header="Ping Status"    body={statusBodyTemplate} sortable filter />
                <Column
                    field="stateElapsedMinutes"
                    header="State Duration"
                    body={(row) => formatElapsed(row.stateElapsedMinutes)}
                    sortable
                />
                <Column
                    field="runTimestamp"
                    header="Last Ping (Local)"
                    body={(row) => parseUTCTimestamp(row.runTimestamp)}
                    sortable
                />
            </DataTable>
        </div>
    );
};

export default DevicePingTable;
